import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import {
  useAnnotateCompanyFileMutation,
  useCompanyFileFieldsQuery,
  usePreviewDocumentSignatureTemplateMutation,
} from 'queries/company'
import { useCompanyDetailContextValue } from '../../../context/CompanyDetailProvider'
import { useCompanyDetailWorkflowsContextValue } from '../../../context/CompanyDetailWorkflowProvider'

type PdfField = NonNullable<
  ReturnType<typeof useCompanyFileFieldsQuery>['data']
>['fields'][number]

type EditCompanyFileSchemaFlowContextState = {
  state: {
    fields: PdfField[]
    pdfFieldKeys: string[]
    isLoadingFields: boolean
    fieldsErrorMessage: string | undefined
    annotateMutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
    previewMutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
      data: Blob | undefined
    }
  }
  actions: {
    annotate: ReturnType<typeof useAnnotateCompanyFileMutation>['mutate']
    resetAnnotate: ReturnType<typeof useAnnotateCompanyFileMutation>['reset']
    preview: ReturnType<
      typeof usePreviewDocumentSignatureTemplateMutation
    >['mutate']
    resetPreview: ReturnType<
      typeof usePreviewDocumentSignatureTemplateMutation
    >['reset']
  }
}

const [Context, useEditCompanyFileSchemaFlowContextValue] =
  createContext<EditCompanyFileSchemaFlowContextState>({
    name: 'EditCompanyFileSchemaFlowContext',
  })

export const EditCompanyFileSchemaFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    state: { id: companyId },
  } = useCompanyDetailContextValue()
  const {
    editCompanyFileSchemaFlow: { file, isOpen },
  } = useCompanyDetailWorkflowsContextValue()

  const fieldsQuery = useCompanyFileFieldsQuery(companyId, file?.id, {
    enabled: isOpen && !!file?.id,
  })
  const annotateMutation = useAnnotateCompanyFileMutation(companyId)
  const previewMutation = usePreviewDocumentSignatureTemplateMutation(companyId)

  const fields = fieldsQuery.data?.fields ?? []
  const pdfFieldKeys = fields.map((field) => `${field.page}.${field.name}`)

  const value: EditCompanyFileSchemaFlowContextState = {
    state: {
      fields,
      pdfFieldKeys,
      isLoadingFields: fieldsQuery.isLoading,
      fieldsErrorMessage: parseErrorMessage(fieldsQuery.error),
      annotateMutationState: {
        isPending: annotateMutation.isPending,
        isError: annotateMutation.isError,
        errorMessage: parseErrorMessage(annotateMutation.error),
      },
      previewMutationState: {
        isPending: previewMutation.isPending,
        isError: previewMutation.isError,
        errorMessage: parseErrorMessage(previewMutation.error),
        data: previewMutation.data,
      },
    },
    actions: {
      annotate: annotateMutation.mutate,
      resetAnnotate: annotateMutation.reset,
      preview: previewMutation.mutate,
      resetPreview: previewMutation.reset,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useEditCompanyFileSchemaFlowContextValue }
